import { useEffect } from 'react'

import { useCommonServices } from '@lib/hooks/services'
import { Cookie, getCookie, setCookie } from '@lib/utils/cookies'

import { getConfig } from '@config/index'

import { APP_VERSION, IS_BROWSER } from '@constants/globals'

import { type PageContextType } from '@contexts/page-context'

interface AnalyticsPayload {
  path: string
  page: string
  user_agent: string
  referrer: string
  url: string
  device_type: 'mobile' | 'desktop'
  is_genesis: boolean
  value: number
  source?: string
  via?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
}

function generateId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replaceAll(/[xy]/g, c => {
    const r = Math.trunc(Math.random() * 16)
    return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

function getDeviceId() {
  const cookie = getCookie(Cookie.WebDevice)
  if (cookie) return cookie

  const id = generateId()
  setCookie(Cookie.WebDevice, id, 60 * 86_400)
  return id
}

function cleanUrl(url: string): string {
  return url.replaceAll(/[?&#](token|auth|secret)=[^?&#]*/gi, '')
}

function trackPageVisit(apiUrl: string, userEmail: string, appVersion: string) {
  const params = new URLSearchParams(location.search)
  let fields: AnalyticsPayload = {
    path: location.pathname,
    page: document.title || location.pathname.split('/').pop() || 'home',
    user_agent: navigator.userAgent,
    referrer: cleanUrl(document.referrer || ''),
    url: cleanUrl(location.href),
    is_genesis: true,
    device_type: /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
    value: Date.now(),
  }

  const trackingParams = ['source', 'via', 'utm_source', 'utm_medium', 'utm_campaign'] as const
  for (const p of trackingParams) {
    if (params.has(p)) {
      fields[p] = params.get(p)!
    }
  }

  fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-superhuman-user-email': userEmail,
      'x-superhuman-device-id': getDeviceId(), // CRITICAL: Must be session-consistent and included in every request
      'x-superhuman-version': appVersion,
    },
    body: JSON.stringify({
      dataset: 'website',
      metrics: [{ name: 'ui.visit', fields }],
    }),
    keepalive: true,
  }).catch(error => console.error('Analytics error:', error))
}

export const useLegacyAnalytics = (pageContext: PageContextType) => {
  const { userService } = useCommonServices()

  useEffect(() => {
    const shouldTrack = getConfig().tracking.shLegacyTracking.enabled && IS_BROWSER

    if (!shouldTrack) {
      return
    }

    const trackPageLoad = () => {
      trackPageVisit(
        getConfig().tracking.shLegacyTracking.apiUrl,
        userService.currentUser.email || 'missing-email@example.com',
        APP_VERSION,
      )
    }

    if (document.readyState === 'complete') {
      trackPageLoad()
    } else {
      window.addEventListener('load', trackPageLoad)

      return () => {
        window.removeEventListener('load', trackPageLoad)
      }
    }
  }, [userService.currentUser.email, pageContext])
}
